"use client";

import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import download from "downloadjs";
import * as htmlToImage from "html-to-image";
import { FaSpotify } from "react-icons/fa6";
import { MdExplicit } from "react-icons/md";

import { cn } from "@/utils/cn";

type Song = {
  isPlaying: boolean;
  title: string;
  artist: string;
  albumImageUrl: string;
  songUrl: string;
  lastPlayedAt: string | null;
  explicit: boolean;
};

export const SpotifyCard = () => {
  const cardRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["spotify-data"],
    queryFn: async () => await axios.get(`/api/now-playing`),
    refetchInterval: 3000,
  });

  const song: Song = data?.data;

  if (!song)
    return <p className="container bg-inherit py-6">Not playing anything.</p>;

  // Download the image
  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current);
      download(dataUrl, song.title);
    } catch (error) {
      console.log("Error generating image:", error);
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        className="container bg-background py-6 dark:bg-foreground"
      >
        {/* Outer Container */}
        <Link
          href={song.songUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="container flex flex-col gap-1 rounded-[32px] bg-neutral-200 p-3 shadow-xs transition-all duration-300 dark:bg-neutral-950"
        >
          {/* Now Playing Indicator */}
          <div className="flex items-center gap-1.5 px-1 py-px text-xs font-semibold text-neutral-400 dark:text-neutral-600">
            <div
              className={cn(
                "size-2 flex-none rounded-full",
                song.isPlaying
                  ? "bg-green-600 dark:bg-green-500"
                  : "bg-neutral-400 dark:bg-neutral-600",
              )}
            />
            {song.isPlaying ? (
              <p>Playing now</p>
            ) : (
              <p>
                Last played{" "}
                {formatDistanceToNow(new Date(song.lastPlayedAt!), {
                  addSuffix: false,
                })}{" "}
                ago
              </p>
            )}
          </div>

          {/* Inner Container */}
          <div className="relative rounded-[20px] border border-neutral-300/50 bg-neutral-100 p-2 shadow transition-all duration-300 ease-in-out dark:border-neutral-800/30 dark:bg-neutral-900/70">
            <div className="flex flex-col gap-4">
              {/* Cover Art */}
              <div className="relative aspect-square w-full flex-shrink-0 overflow-hidden rounded-[12px] border-neutral-200/50 shadow dark:border-neutral-800/50">
                <Image
                  src={song.albumImageUrl}
                  alt="Album cover"
                  fill
                  className="object-cover object-center"
                />
              </div>

              {/* Title and Artiste(s) */}
              <div className="flex flex-col gap-0.5 pb-1">
                <p className="line-clamp-1 text-base leading-snug font-extrabold md:text-lg">
                  {song.title}
                </p>
                <p className="line-clamp-1 flex items-center gap-1 text-xs leading-snug text-neutral-600 dark:text-neutral-400">
                  {song.explicit ? (
                    <MdExplicit className="size-5 shrink-0 text-neutral-500 dark:text-neutral-500" />
                  ) : null}
                  <span
                    className={cn(
                      "mt-0.5 line-clamp-1",
                      !song.explicit && "mt-0",
                    )}
                  >
                    {song.artist}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Download Song Image Button */}
      <div
        onClick={handleDownload}
        className="fixed top-0 right-0 size-6 cursor-pointer opacity-0"
      >
        <FaSpotify className="size-full" />
      </div>
    </>
  );
};
